import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingEmissionFactorsRefrigerants,
  useGetFootprintingFarmPrimaryDataById,
  useGetFootprintingFarmRefrigerantUsage,
  useMutateFootprintingFarmPrimaryData,
  useMutateFootprintingFarmRefrigerantUsage,
  useTranslation,
} from "utils";

import { UploadableRefrigerant } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputLabel,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Switch,
  Tooltip,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const refrigerantPlaceholder: UploadableRefrigerant = {
  id: undefined,
  emissionFactorRefrigerantId: "",
  amountKg: null,
  usedForDairy: false,
};

export const FarmRefrigerantsForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const selectGroupName = "footprinting-farm-refrigerants";
  const [refrigerantsActive, setRefrigerantsActive] = useState<boolean | null>(null);
  const [selectedRefrigerants, setSelectedRefrigerants] = useState<UploadableRefrigerant[]>([]);

  const farmPrimaryDataQuery = useGetFootprintingFarmPrimaryDataById(primaryDataId ?? "");

  const refrigerantsQuery = useGetFootprintingEmissionFactorsRefrigerants();
  const farmRefrigerantUsageQuery = useGetFootprintingFarmRefrigerantUsage(primaryDataId ?? "");

  const { mutate: mutateFarmRefrigerantUsage } = useMutateFootprintingFarmRefrigerantUsage(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedRefrigerants: selectedRefrigerants.filter((r) => !!r.emissionFactorRefrigerantId),
    },
    primaryDataId
  );

  const { mutate: mutateFarmPrimaryData } = useMutateFootprintingFarmPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      refrigerantsActive,
    },
    primaryDataId
  );

  const isLoading = [farmPrimaryDataQuery, farmRefrigerantUsageQuery, refrigerantsQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && farmPrimaryDataQuery.data?.id) {
        mutateFarmPrimaryData();
        mutateFarmRefrigerantUsage();
      }
    },
    [farmPrimaryDataQuery.data?.id, isLoading, mutateFarmPrimaryData, mutateFarmRefrigerantUsage, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (farmRefrigerantUsageQuery.data && !!farmRefrigerantUsageQuery.data.length) {
      setSelectedRefrigerants(
        farmRefrigerantUsageQuery.data
          .filter((fru) => fru.id !== undefined)
          .map((refrigerant) => ({
            ...refrigerant,
          }))
      );
    } else if (!queryIsLoading(farmRefrigerantUsageQuery)) {
      // If we're not loading but the user has refrigerants active,
      // set the selectedRefrigerants to the placeholder
      setSelectedRefrigerants([{ ...refrigerantPlaceholder }]);
    }
  }, [farmRefrigerantUsageQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (farmPrimaryDataQuery.data && farmPrimaryDataQuery.isSuccess) {
      setRefrigerantsActive(farmPrimaryDataQuery.data.refrigerantsActive ?? null);
      const currentFormValues = formRef.current?.getFieldsValue() || {};

      formRef.current?.setFieldsValue({
        refrigerantsActive: farmPrimaryDataQuery.data.refrigerantsActive ?? null,
        ...currentFormValues,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = farmPrimaryDataQuery.data.refrigerantsActive !== null;

      if (fieldsToValidate && formRef.current) {
        formRef.current.validateFields();
      }
    }
  }, [farmPrimaryDataQuery.data, farmPrimaryDataQuery.isSuccess]);

  // This ensures that all form fields rerender when the non-last refrigerant
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (selectedRefrigerants.length > 0) {
      formRef.current?.resetFields();
    }
  }, [selectedRefrigerants.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const disabledRefrigerants = selectedRefrigerants.reduce((acc, r) => ({ ...acc, [r.emissionFactorRefrigerantId]: true }), {} as Record<string, boolean>);

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-200 padding-right-200">{t("coolingForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("coolingForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-farm-refrigerants-form" className="margin-bottom-100 padding-200">
        <Form ref={formRef}>
          <Flex align="center" gap={16} justify="space-between">
            <span style={{ maxWidth: "60%" }}>
              {t("coolingForm.question.refrigerantsUsed")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("coolingForm.tooltips.refrigerantsUsed")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              name="refrigerantsActive"
              dataTestId="footprinting-farm-refrigerants-active-radio"
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setRefrigerantsActive(target.value);
              }}
              value={refrigerantsActive}
              defaultValue={refrigerantsActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {refrigerantsActive &&
            selectedRefrigerants.map((refrigerant, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;
              const updateItem = (item: UploadableRefrigerant) => {
                const next = [...selectedRefrigerants];
                next[index] = { ...item };
                setSelectedRefrigerants(next);
              };
              return (
                <div key={index} className="padding-top-200">
                  <Flex className="margin-bottom-100" justify="space-between" vertical={false}>
                    <h3 style={{ flex: 1 }}>{t("coolingForm.question.title") + (index + 1)}</h3>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setSelectedRefrigerants([...selectedRefrigerants].filter((_, i) => i !== index))}
                    />
                  </Flex>

                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    className="margin-bottom-100"
                    name={selectGroupWithIndex}
                    disabled={readOnly}
                    dataTestId={selectGroupWithIndex}
                    label={t("coolingForm.question.refrigerantType")}
                    tooltip={t("coolingForm.tooltips.refrigerantType")}
                    rules={[{ required: true, message: false }]}
                    initialValue={refrigerant.emissionFactorRefrigerantId}
                    value={refrigerant.emissionFactorRefrigerantId}
                    onChange={(v) => updateItem({ ...refrigerant, emissionFactorRefrigerantId: v })}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    allowClear
                  >
                    {(refrigerantsQuery.data ?? []).map((refrigerant) => (
                      <Option
                        disabled={readOnly || disabledRefrigerants[refrigerant.id]}
                        key={`${selectGroupWithIndex}-${refrigerant.id}`}
                        data-testid={`${selectGroupWithIndex}-${refrigerant.id}`}
                        value={refrigerant.id}
                        label={refrigerant.name}
                      >
                        {refrigerant.name}
                      </Option>
                    ))}
                  </Select>

                  <InputNumber
                    name={`${selectGroupWithIndex}-amount`}
                    dataTestId={`${selectGroupWithIndex}-amount`}
                    label={t("coolingForm.question.refrigerantAmount")}
                    tooltip={t("coolingForm.tooltips.refrigerantAmount")}
                    suffix="kg"
                    disabled={readOnly}
                    value={refrigerant.amountKg}
                    initialValue={refrigerant.amountKg}
                    onChange={(v) => updateItem({ ...refrigerant, amountKg: v })}
                    rules={[
                      { required: true, message: t("coolingForm.question.refrigerantQuestionError") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                        message: t("coolingForm.question.refrigerantQuestionError"),
                      },
                    ]}
                  />

                  {carbonProgramQuery.data?.platformFeatures?.footprinting?.dairy === true ? (
                    <Flex className="margin-top-100" align="center" justify="space-between">
                      <InputLabel
                        label={t("coolingForm.question.refrigerantUsedForDairy")}
                        required
                        tooltip={t("coolingForm.tooltips.refrigerantUsedForDairy")}
                      />
                      <Switch disabled={readOnly} checked={refrigerant.usedForDairy} onChange={(v) => updateItem({ ...refrigerant, usedForDairy: v })} />
                    </Flex>
                  ) : null}
                </div>
              );
            })}
        </Form>
        {refrigerantsActive && (
          <Button
            type="text"
            disabled={readOnly}
            className="margin-top-125 padding-left-25"
            onClick={() => {
              setSelectedRefrigerants([...selectedRefrigerants, { ...refrigerantPlaceholder }]);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("coolingForm.add")}
          </Button>
        )}
      </Card>
    </div>
  );
};
